import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/hooks/redux'
import { login } from '@/store/slices/authSlice'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faTerminal } from '@fortawesome/free-solid-svg-icons'

/**
 * Login page for SuperAdmin authentication
 * Dark mode geek aesthetic
 */
export default function LoginPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setErrorMsg] = useState<string | null>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget
    setFormData((prev) => ({ ...prev, [name]: value }))
    setErrorMsg(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrorMsg(null)

    try {
      const result = await dispatch(login(formData))
      
      if (login.fulfilled.match(result)) {
        // Login successful, navigate to dashboard
        navigate('/')
      } else if (login.rejected.match(result)) {
        // Login failed
        setErrorMsg((result.payload as string) || t('common.error'))
      }
    } catch (error: any) {
      const message = error.message || error.response?.data?.message || t('common.error')
      setErrorMsg(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 relative overflow-hidden">
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-linear-to-b from-cyan-500/20 to-transparent" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_1px,rgba(15,23,42)_1px)] bg-size-[50px_50px]" />
      </div>

      <div className="relative w-full max-w-lg">
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-linear-to-r from-cyan-500 via-purple-500 to-red-500 rounded-2xl opacity-20 blur-2xl" />
        
        {/* Card */}
        <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <FontAwesomeIcon icon={faTerminal} className="text-cyan-400 text-2xl" />
              <h1 className="text-3xl font-black bg-linear-to-r from-cyan-400 via-purple-400 to-red-500 bg-clip-text text-transparent">
                KaraoNow
              </h1>
            </div>
            <p className="text-sm font-mono text-slate-400">
              {'> '}<span className="text-cyan-400">superadmin</span>{' --access'}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
              <p className="text-sm text-red-300 font-mono">
                <span className="text-red-500">{'[ERROR]'}</span> {error}
              </p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                {t('common.email')}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="w-full px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                placeholder="admin@karaonow.com"
                disabled={loading}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
                {t('common.password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition"
                  placeholder="••••••••"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-400 transition"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 px-4 py-2.5 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block animate-spin">⟳</span>
                  {t('common.loading')}
                </span>
              ) : (
                t('auth.login')
              )}
            </button>
          </form>

          {/* Info panel */}
          <div className="mt-6 p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg">
            <p className="text-xs text-slate-400 font-mono leading-relaxed">
              <span className="text-purple-400">{'> INFO:'}</span> {t('superadmin.loginInfo')}
            </p>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center">
            <p className="text-xs text-slate-500 font-mono">KaraoNow © 2026</p>
          </div>
        </div>
      </div>
    </div>
  )
}

